import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TrashIcon, DownloadIcon, IconButton } from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useFileTokenContext } from "~/components/FilePreview/context/FileTokenContext.js";
import { DropboxAttachmentIcon, GoogleDriveAttachmentIcon } from "~/components/svg";
import { getAttachmentById } from "../../../../../store/selectors/selector.attachments.jsx";
import {
  isDropboxFile as getIsDropboxFile,
  isGoogleDriveFile as getIsGoogleDriveFile,
  isLocalFile as getIsLocalFile,
  isWarehouseFile as getIsWarehouseFile,
} from "../../../../../utils/files.jsx";
import { prepareThumbnailUrl } from "../../../../../utils/utils.jsx";
import DownloadLink from "../../../../component.download_link.jsx";
import { useConfirmDialogOption } from "../../../MenuOptions/index.jsx";

export const MessageAttachment = ({ id, className, onClick, onRemove, size, isFromLoggedUser }) => {
  const { t } = useTranslation();
  const { preview_token, download_token, thumb_token } = useFileTokenContext();

  const attachment = useSelector((state) => getAttachmentById(state, id));

  const type = attachment?.get(`class`);

  const fileType = useMemo(() => {
    if (getIsLocalFile(type)) {
      return "localFile";
    }
    if (getIsWarehouseFile(type)) {
      return "warehouseFile";
    }
    if (getIsDropboxFile(type)) {
      return "dropboxFile";
    }
    if (getIsGoogleDriveFile(type)) {
      return "googleDriveFile";
    }
  }, [type]);

  const isDropboxOrGoogleDriveFile = fileType === "googleDriveFile" || fileType === "dropboxFile";
  const isLocalOrWarehouseFile = fileType === "localFile" || fileType === "warehouseFile";

  const handleOnClick = useCallback(() => {
    typeof onClick === "function" && onClick(id);
  }, [onClick, id]);

  const thumbnailUrl = useMemo(
    () => prepareThumbnailUrl(attachment?.get(`thumbnail_url`), size, size, "crop", thumb_token, download_token),
    [attachment, download_token, size, thumb_token]
  );

  const handleRemove = useCallback(() => {
    typeof onRemove === "function" && isFromLoggedUser && onRemove(id);
  }, [onRemove, id, isFromLoggedUser]);

  const [onShowRemoveAttachmentDialog, template] = useConfirmDialogOption({
    dialogTitle: t("Delete File"),
    dialogContent: t("The file will be permanently deleted from the conversation."),
    confirmBtnText: t("Delete"),
    onConfirmSuccess: handleRemove,
  });

  const downloadUrl = useMemo(() => {
    if (isLocalOrWarehouseFile) {
      return attachment
        ?.get(`download_url`)
        .replace("--PREVIEW-TOKEN--", preview_token)
        .replace("--DOWNLOAD-TOKEN--", download_token);
    }

    if (isDropboxOrGoogleDriveFile) {
      return attachment?.get(`preview_url`).replace("--PREVIEW-TOKEN--", preview_token);
    }

    return undefined;
  }, [isLocalOrWarehouseFile, isDropboxOrGoogleDriveFile, attachment, preview_token, download_token]);

  return (
    <div className={classnames("message-attachment tw-relative tw-overflow-hidden", className)}>
      <div className="attachment-options tw-absolute">
        {isLocalOrWarehouseFile ? (
          <DownloadLink url={downloadUrl}>
            <IconButton variant="dark transparent">
              <DownloadIcon />
            </IconButton>
          </DownloadLink>
        ) : null}
        {isDropboxOrGoogleDriveFile ? (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
          >
            <IconButton variant="dark transparent">
              <DownloadIcon />
            </IconButton>
          </a>
        ) : null}
        {isFromLoggedUser ? (
          <IconButton
            className="tw-ml-1"
            variant="dark transparent"
            onClick={onShowRemoveAttachmentDialog}
          >
            <TrashIcon />
          </IconButton>
        ) : null}
      </div>
      <div
        className="tw-cursor-pointer tw-h-full message-attachment-content"
        onClick={handleOnClick}
        title={attachment?.name}
      >
        {isDropboxOrGoogleDriveFile ? (
          <div className="attachment_preview_content tw-h-full tw-flex tw-items-center tw-justify-center">
            {fileType === "googleDriveFile" ? <GoogleDriveAttachmentIcon /> : null}
            {fileType === "dropboxFile" ? <DropboxAttachmentIcon /> : null}
          </div>
        ) : null}
        {isLocalOrWarehouseFile ? (
          <img
            src={thumbnailUrl}
            alt={attachment?.name}
            className="tw-w-full tw-h-full tw-object-cover"
          />
        ) : null}
      </div>
      {template}
    </div>
  );
};

MessageAttachment.propTypes = {
  id: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  size: PropTypes.number,
  isFromLoggedUser: PropTypes.bool,
  className: PropTypes.string,
  onRemove: PropTypes.func,
};

MessageAttachment.defaultProps = {
  size: 500,
  isFromLoggedUser: false,
};
